import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";
import { Tabs } from "@/components/Tabs";

export interface NotificationItem {
    id: string | number;
    title: string;
    body: string;
    action_url?: string | null;
    read_at?: string | null;
    created_at?: string | null;
}

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCountUpdated?: (unreadCount: number) => void;
    unreadCount?: number;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
    isOpen,
    onClose,
    onCountUpdated,
    unreadCount: initialUnreadCount = 0,
}) => {
    const { t } = useTranslation("global");
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [activeTab, setActiveTab] = useState<"unread" | "all">("unread");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isMarkingAll, setIsMarkingAll] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            try {
                const saved = localStorage.getItem("vigepra_notifications");
                if (saved) {
                    const parsed: NotificationItem[] = JSON.parse(saved);
                    setNotifications(parsed);
                    const unread = parsed.filter((n) => !n.read_at).length;
                    onCountUpdated?.(unread);
                } else {
                    setNotifications([]);
                    onCountUpdated?.(0);
                }
            } catch {
                setNotifications([]);
            } finally {
                setIsLoading(false);
            }
        }
    }, [isOpen]);

    const handleMarkAllRead = (): void => {
        setIsMarkingAll(true);
        const updated = notifications.map((n) => ({
            ...n,
            read_at: n.read_at || new Date().toISOString(),
        }));
        setNotifications(updated);
        localStorage.setItem("vigepra_notifications", JSON.stringify(updated));
        onCountUpdated?.(0);
        setIsMarkingAll(false);
    };

    const handleItemClick = (item: NotificationItem): void => {
        if (!item.read_at) {
            const updated = notifications.map((n) =>
                n.id === item.id
                    ? { ...n, read_at: new Date().toISOString() }
                    : n
            );
            setNotifications(updated);
            localStorage.setItem(
                "vigepra_notifications",
                JSON.stringify(updated)
            );
            onCountUpdated?.(
                Math.max(0, updated.filter((n) => !n.read_at).length)
            );
        }

        if (item.action_url) {
            onClose();
            if (item.action_url.startsWith("http")) {
                window.open(item.action_url, "_blank");
            } else {
                navigate(item.action_url);
            }
        }
    };

    const unreadCount = notifications.filter((n) => !n.read_at).length;
    const displayedNotifications =
        activeTab === "unread"
            ? notifications.filter((n) => !n.read_at)
            : notifications;

    const customFooter = (
        <div className="flex items-center justify-center space-x-2.5 w-full">
            {unreadCount > 0 && (
                <button
                    type="button"
                    onClick={handleMarkAllRead}
                    disabled={isMarkingAll}
                    className="btn secondary flex items-center space-x-1.5"
                >
                    <Icon value="checkCheck" size={14} className="shrink-0" />
                    <span>{t("notifications.mark_all_read")}</span>
                </button>
            )}

            <button type="button" onClick={onClose} className="btn secondary">
                {t("notifications.close")}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("notifications.modal_title")}
            maxWidth="md"
            footer={customFooter}
        >
            <div className="space-y-3 pb-1 text-left">
                {/* Unread vs All */}
                <div className="flex justify-center w-full">
                    <Tabs<"unread" | "all">
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        scroll={false}
                        activeBgClass="bg-primary"
                        tabs={[
                            {
                                id: "unread",
                                label: t("notifications.tab_unread"),
                                badge: unreadCount,
                            },
                            { id: "all", label: t("notifications.tab_all") },
                        ]}
                    />
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                        <Icon
                            value="loader"
                            size={28}
                            className="animate-spin text-primary mb-2"
                        />
                        <span className="text-xs font-medium">
                            {t("common.loading")}
                        </span>
                    </div>
                ) : displayedNotifications.length === 0 ? (
                    <div className="text-center py-10 px-2">
                        <div className="w-14 h-14 rounded-2xl bg-blurred text-primary flex items-center justify-center mx-auto mb-3">
                            <Icon value="inbox" size={28} />
                        </div>
                        <h4 className="text-sm font-bold text-slate-800">
                            {t("notifications.empty_title")}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto leading-relaxed">
                            {t("notifications.empty_desc")}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2.5 max-h-80 overflow-y-auto">
                        {displayedNotifications.map((item) => {
                            const isUnread = !item.read_at;
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleItemClick(item)}
                                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-start gap-3 ${
                                        isUnread
                                            ? "bg-primary/5 border-primary/20 shadow-xs hover:bg-primary/10"
                                            : "bg-white border-slate-200/80 shadow-2xs hover:bg-slate-50"
                                    }`}
                                >
                                    <div
                                        className={`p-2 rounded-xl shrink-0 mt-0.5 ${
                                            isUnread
                                                ? "bg-primary/15 text-primary"
                                                : "bg-slate-100 text-slate-400"
                                        }`}
                                    >
                                        <Icon
                                            value="bell"
                                            size={16}
                                            className="text-primary"
                                        />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <p
                                                className={`text-xs font-bold truncate ${
                                                    isUnread
                                                        ? "text-slate-900"
                                                        : "text-slate-700"
                                                }`}
                                            >
                                                {item.title}
                                            </p>
                                            {item.created_at && (
                                                <span className="text-[10px] text-slate-400 shrink-0 font-medium">
                                                    {new Date(
                                                        item.created_at
                                                    ).toLocaleDateString([], {
                                                        day: "numeric",
                                                        month: "short",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-xs text-slate-600 mt-1 leading-relaxed break-words">
                                            {item.body}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>
    );
};
